import { BadInputError } from '@watershed/errors/BadInputError';
import { UnexpectedError } from '@watershed/errors/UnexpectedError';
import assertNever from '@watershed/shared-util/src/assertNever';

import { normalizeDate } from '../../emissionsModel/utils';
import { getNonDateKeyColumns } from '../../referenceData/ReferenceDataSchemaInterface';
import { difference } from '../../set';
import { doubleQuote } from '../../sql';
import { sqlIdentifier } from '../../sqlTemplate';
import { parseUnitString } from '../../unit';
import { assertConstantValue } from '../assert';
import { ASTCall, ASTExpr, ASTKwarg, canonicalMatchOrder } from '../ast';
import { evalExpression } from '../eval';
import {
  ExprType,
  makeNullable,
  NumberType,
  QuantityType,
  stringifyType,
} from '../expr_types';
import { extractStringLiteral, isNumberLiteral } from '../literal';
import { Quantity } from '../quantity';
import { findMatchingRow, MatchArgs } from '../refData';
import { assertNonStructColumnType } from '../row_types';
import { addRef, rowIdCol, traceHelper } from '../sql/internal';
import { l } from '../template';
import { TraceASTReference, TraceASTType } from '../trace';
import {
  assertArgShape,
  isSameType,
  TypecheckWarning,
  typecheckExpression,
} from '../typecheck';
import { Context, Func, Macro, SqlExpression } from '../types';
import { macro } from './helpers/macros';

function isValidRefMonth(exprType: ExprType): boolean {
  const { ty } = baseType(exprType);
  switch (ty) {
    case 'string':
    case 'number':
    case 'null':
    case 'date':
    case 'datetime':
      return true;

    case 'boolean':
    case 'list':
    case 'quantity':
      return false;

    default:
      assertNever(ty);
  }
}

function quantityTypeToNumberType(ty: QuantityType): NumberType {
  if (ty.units.length > 1) {
    throw new UnexpectedError(
      'Cannot convert quantity with more than one unit to a number',
      { data: { ty } }
    );
  }
  return { ty: 'number', unit: ty.units[0] };
}

export const ref: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings?: Array<TypecheckWarning>
  ): ExprType {
    assertArgShape(call, 2, 3);

    const slug = extractStringLiteral(call.args[0]);
    if (slug.startsWith('r!')) {
      warnings?.push(
        `The \`r!\` syntax for reference data slugs is deprecated: replace \`"${slug}"\` with \`"${slug.slice(
          2
        )}"\``
      );
    }

    const col = extractStringLiteral(call.args[1]);
    const month: ExprType | undefined =
      call.args[2] && typecheckExpression(ctx, call.args[2], warnings);

    if (month) {
      BadInputError.invariant(
        isValidRefMonth(month),
        `The 'month' argument to ref() must be a string, number, date, datetime, or null; got ${
          baseType(month).ty
        }`
      );
    }

    validateKeys(ctx, slug, call, warnings);

    const rowType = ctx.refTypes.get(slug);
    const rowColumnType = rowType?.get(col);

    if (!rowType) {
      warnings?.push(`No schema found for ${slug}; assuming numeric`);
      return { ty: 'number' };
    } else if (!rowColumnType) {
      warnings?.push(
        `No property ${col} found in schema for ${slug}; assuming numeric`
      );
      return { ty: 'number' };
    }

    assertNonStructColumnType(rowColumnType);

    for (const [name, { t, expr }] of call.kwargs.entries()) {
      const schemaType = rowType.get(name);
      BadInputError.invariant(
        schemaType,
        `No property \`${name}\` found in schema for \`${slug}\``
      );

      assertNonStructColumnType(schemaType); // there are no structs columns in ref data

      const exprType = typecheckExpression(ctx, expr, warnings);
      const schemaBaseType = baseType(schemaType);
      const fuzziedSchemaType =
        t === 'exact' ? schemaType : makeNullable(schemaType);

      if (schemaBaseType.ty === 'quantity' && schemaBaseType.units.length > 1) {
        warnings?.push(
          `\`ref\` keyword argument \`${name}\` is invalid: the column is of type \`${stringifyType(
            schemaType
          )}\`, and quantity columns with variable units cannot be used as lookup keys`
        );
        continue;
      }

      if (
        (schemaBaseType.ty === 'number' || schemaBaseType.ty === 'quantity') &&
        isNumberLiteral(expr)
      ) {
        continue;
      }
      if (schemaBaseType.ty === 'null') {
        continue;
      }
      if (fuzziedSchemaType.ty === 'nullable' && exprType.ty === 'null') {
        continue;
      }
      if (
        isSameType(exprType, schemaType) ||
        (schemaType.ty === 'nullable' &&
          isSameType(exprType, schemaType.baseTy)) ||
        (fuzziedSchemaType.ty === 'nullable' &&
          isSameType(exprType, fuzziedSchemaType))
      ) {
        continue;
      }
      if (
        schemaBaseType.ty === 'quantity' &&
        exprType.ty === 'number' &&
        schemaBaseType.units.length <= 1 &&
        isSameType(quantityTypeToNumberType(schemaBaseType), exprType)
      ) {
        continue;
      }

      warnings?.push(
        `Argument \`${name}\` of type \`${stringifyType(
          exprType
        )}\` is incompatible with the schema's type for that column, \`${stringifyType(
          schemaType
        )}\``
      );
    }

    // `RowType` quantities have a superset of the properties of `ExprType`, so
    // trim it.
    if (rowColumnType.ty === 'quantity') {
      return {
        ty: 'quantity',
        units: rowColumnType.units,
      };
    }
    return rowColumnType;
  },

  eval(ctx: Context, call: ASTCall): unknown {
    return innerEval(ctx, call);
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    const ty = typecheckExpression(ctx, call);
    const slug = extractStringLiteral(call.args[0]);
    const col = extractStringLiteral(call.args[1]);

    // List of columns we need from this reference
    const cols = [col];
    const refTy = ctx.refTypes.get(slug)?.get(col);

    if (refTy?.ty === 'quantity') {
      cols.push(refTy.column);
    }

    const refSql = addRef(ctx, call, cols);

    return traceHelper<TraceASTReference>(
      ty,
      {
        expr: doubleQuote(`${refSql.name}.${col}`),
        stage: refSql.stage,
        unitExpr:
          refTy?.ty === 'quantity'
            ? doubleQuote(`${refSql.name}.${refTy.column}`)
            : null,
        error: refSql.error,
      },
      () => ({
        t: TraceASTType.TReference,
        s: slug,
        c: col,
        i: sqlIdentifier(rowIdCol(refSql.name)),
      })
    );
  },
};
/** Type for reference call parameters */
export type RefCallParams = {
  slug: string;
  col: string;
  matchArgs: MatchArgs;
};

/** Extract all parameters needed for a reference call */
export function extractRefCallParams(
  ctx: Context,
  call: ASTCall
): RefCallParams {
  const args: ReadonlyArray<ASTExpr> = call.args;
  const slug = extractStringLiteral(args[0]);
  const col = extractStringLiteral(args[1]);

  const canonical = canonicalMatchOrder(call);
  const canonicalArgs: ReadonlyArray<ASTExpr> = canonical.args;
  const kwargs: ReadonlyMap<string, ASTKwarg> = canonical.kwargs;

  BadInputError.invariant(
    canonicalArgs.length >= 2 && canonicalArgs.length <= 3,
    'Expected 2 input args'
  );

  // Process keyword arguments
  const matchArgs: MatchArgs = Array.from(kwargs).map(
    ([key, { t, expr: value }]) => {
      return [key, assertConstantValue(evalExpression(ctx, value)), t];
    }
  );

  // Handle date range arguments if present
  if (canonicalArgs[2]) {
    const refType = ctx.refTypes.get(slug);
    UnexpectedError.invariant(
      refType,
      `No pre-loaded reference data for ${slug}. You must populate ctx.refTypes before calling eval`
    );

    let month = evalExpression(ctx, canonicalArgs[2]);

    if (month === null) {
      matchArgs.push(['start_date', null, 'exact']);
      matchArgs.push(['end_date', null, 'exact']);
    } else {
      month = normalizeDate(month);

      BadInputError.invariant(month instanceof Date, 'Expected a date');
      if (refType.get('start_date')) {
        matchArgs.push(['start_date', new Date(month), 'lessEqualDate']);
      }
      if (refType.get('end_date')) {
        matchArgs.push(['end_date', new Date(month), 'greaterThanDate']);
      }
    }
  }

  return { slug, col, matchArgs };
}

function innerEval(ctx: Context, call: ASTCall) {
  const { slug, col, matchArgs } = extractRefCallParams(ctx, call);

  const db = ctx.refData.get(slug);
  UnexpectedError.invariant(
    db,
    `No pre-loaded reference data for ${slug}. You must populate ctx.refData before calling eval`
  );

  const refType = ctx.refTypes.get(slug);
  UnexpectedError.invariant(
    refType,
    `No pre-loaded reference data for ${slug}. You must populate ctx.refTypes before calling eval`
  );

  const matchingRow = findMatchingRow(db, matchArgs);
  if (!matchingRow) {
    return null;
  }

  const ty = refType.get(col);
  // TODO: we should *always* have refTypes
  if (ty && ty.ty === 'quantity') {
    return new Quantity(
      matchingRow[col],
      parseUnitString(matchingRow[ty.column])
    );
  }

  return matchingRow[col];
}

// Throws an error if a user is only specifying a subset of the keys required
// for uniqueness. Adds a warning if a user is specifying keys that are
// not part of the set of unique keys in the schema.
function validateKeys(
  ctx: Context,
  slug: string,
  call: ASTCall,
  warnings?: Array<TypecheckWarning>
): void {
  const uniqueKeys = new Set(ctx.refUniqueKeys.get(slug) ?? []);
  const specifiedKeys = new Set(call.kwargs.keys());

  // 'month' in ref() translates to `start_date` and `end_date` in ref data
  if (call.args.length >= 3) {
    specifiedKeys.add('start_date');
    specifiedKeys.add('end_date');
  }

  const extraKeys = [...difference(specifiedKeys, uniqueKeys)];
  const missingKeys = [...difference(uniqueKeys, specifiedKeys)];
  const missingNonDateKeys = getNonDateKeyColumns(missingKeys);

  if (extraKeys.length > 0 && missingKeys.length > 0) {
    // In the future we can disallow this once we suport having multiple unique
    // constraints for ref data. For now, since we only support one set of
    // unique keys, we need an escape hatch for ref data that's used with
    // multiple sets of lookup keys (e.g. bidirectionally)
    warnings?.push(
      `Found keyword arguments to \`ref()\` which are not part of the unique keys specified in the schema for \`${slug}\`: ${extraKeys
        .map((k) => `\`${k}\``)
        .join(
          ', '
        )}. You may get a runtime error if this lookup results in duplicate rows.`
    );
    return;
  }

  if (missingKeys.length > missingNonDateKeys.length) {
    throw new BadInputError(
      `Expected a 'month' argument to \`ref()\` since the schema for \`${slug}\` contains date columns.`
    );
  }

  if (missingNonDateKeys.length > 0) {
    throw new BadInputError(
      `Expected the following keyword arguments to \`ref()\` since the schema for \`${slug}\` contains them as unique keys: ${missingNonDateKeys
        .map((k) => `\`${k}\``)
        .join(', ')}`
    );
  }
}

export const refSilent: Macro = macro({
  expand(call: ASTCall): ASTExpr {
    const refCall: ASTCall = {
      t: 'call',
      name: 'ref',
      args: call.args,
      kwargs: call.kwargs,
    };
    return l`iferror(${refCall}, null)`;
  },
});

function baseType(type: ExprType) {
  if (type.ty === 'nullable') {
    return type.baseTy;
  }

  return type;
}
